import React, { useEffect, useState, useCallback } from 'react';
import { Search, Eye, Filter, Calendar, Video, Clock, CheckCircle, XCircle, User, Building2 } from 'lucide-react';
import api from '../utils/api';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const MissionApplicationsList = () => {
    const [applications, setApplications] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('interview_accepted'); // Default to what needs action
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [scheduleData, setScheduleData] = useState({
        interviewDate: '',
        interviewLink: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchApplications = useCallback(async () => {
        try {
            setIsLoading(true);
            // Get all mission applications
            const res = await api.get('/mission-application');

            // Client-side filtering/sorting for now as the API returns all
            let data = res.data;

            if (filterStatus) {
                data = data.filter(app => app.status === filterStatus);
            }

            if (search) {
                const searchLower = search.toLowerCase();
                data = data.filter(app =>
                    app.candidate?.first_name?.toLowerCase().includes(searchLower) ||
                    app.candidate?.last_name?.toLowerCase().includes(searchLower) ||
                    app.mission?.title?.title?.toLowerCase().includes(searchLower) ||
                    app.mission?.company?.company_name?.toLowerCase().includes(searchLower)
                );
            }

            // Sort by Date (newest first)
            data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

            setApplications(data);
        } catch (error) {
            console.error('Error fetching applications:', error);
        } finally {
            setIsLoading(false);
        }
    }, [filterStatus, search]);

    useEffect(() => {
        document.title = 'Candidatures Missions - Admin Tekhdem';
        fetchApplications();
    }, [fetchApplications]);

    const handleScheduleClick = (app) => {
        setSelectedApplication(app);
        setScheduleData({
            interviewDate: '',
            interviewLink: ''
        });
        setShowScheduleModal(true);
    };

    const handleScheduleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedApplication) return;

        try {
            setIsSubmitting(true);
            await api.put(`/mission-application/${selectedApplication._id}/schedule-interview`, {
                interviewDate: scheduleData.interviewDate,
                interviewLink: scheduleData.interviewLink
            });
            setShowScheduleModal(false);
            fetchApplications(); // Refresh list
            alert('Entretien programmé avec succès !');
        } catch (error) {
            console.error('Error scheduling interview:', error);
            alert("Erreur lors de la programmation de l'entretien.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Candidatures Missions</h1>
                    <p className="text-slate-500 dark:text-slate-400 text-sm">Gestion des candidatures et des entretiens</p>
                </div>
                <div className="flex gap-2">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Rechercher..."
                            className="pl-9 pr-4 py-2 border border-slate-200 dark:border-slate-800 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-slate-900 w-64 transition-all"
                        />
                    </div>
                    <div className="relative">
                        <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="pl-9 pr-8 py-2 border border-slate-200 dark:border-slate-800 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-slate-900 appearance-none cursor-pointer"
                        >
                            <option value="">Tous les status</option>
                            <option value="pending">En attente</option>
                            <option value="interview_accepted">Entretien Demandé</option>
                            <option value="interview_pending">Entretien Programmé</option>
                            <option value="interview_process">Entretien En Cours</option>
                            <option value="accepted">Accepté</option>
                            <option value="rejected">Rejeté</option>
                            <option value="validated">Validé</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* List */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-slate-50/50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Candidat</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Mission / Entreprise</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Date Candidature</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Statut</th>
                                <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {isLoading ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center">
                                        <div className="inline-block w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
                                    </td>
                                </tr>
                            ) : applications.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                                        Aucune candidature trouvée.
                                    </td>
                                </tr>
                            ) : (
                                applications.map((app) => (
                                    <tr key={app._id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500">
                                                    <User className="w-4 h-4" />
                                                </div>
                                                <div>
                                                    <div className="font-medium text-slate-900 dark:text-white">
                                                        {app.candidate?.first_name} {app.candidate?.last_name}
                                                    </div>
                                                    <div className="text-xs text-slate-500">{app.candidate?.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col">
                                                <span className="font-medium text-slate-900 dark:text-white text-sm">
                                                    {app.mission?.title?.title || 'Mission'}
                                                </span>
                                                <div className="flex items-center gap-1 text-slate-500 text-xs mt-0.5">
                                                    <Building2 className="w-3 h-3" />
                                                    {app.mission?.company?.company_name || 'Entreprise'}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="text-sm text-slate-600 dark:text-slate-400">
                                                {format(new Date(app.createdAt), 'dd MMM yyyy', { locale: fr })}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <StatusBadge status={app.status} />
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            {app.status === 'interview_accepted' && (
                                                <button
                                                    onClick={() => handleScheduleClick(app)}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded-lg transition-colors"
                                                >
                                                    <Calendar className="w-3.5 h-3.5" />
                                                    Programmer Entretien
                                                </button>
                                            )}
                                            {app.status === 'interview_pending' && (
                                                <span className="text-xs text-indigo-600 font-medium">
                                                    Programmé le {format(new Date(app.interviewDate), 'dd/MM HH:mm')}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Schedule Modal */}
            {showScheduleModal && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-white dark:bg-slate-900 rounded-xl w-full max-w-md shadow-2xl animate-in fade-in zoom-in-95 duration-200">
                        <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center">
                            <h2 className="text-lg font-bold text-slate-900 dark:text-white">Programmer l'entretien</h2>
                            <button onClick={() => setShowScheduleModal(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors">
                                <XCircle className="w-5 h-5 text-slate-500" />
                            </button>
                        </div>
                        <form onSubmit={handleScheduleSubmit} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                                    Date et Heure <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="datetime-local"
                                    className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 focus:ring-2 focus:ring-slate-900 dark:focus:ring-white focus:border-transparent transition-all"
                                    required
                                    value={scheduleData.interviewDate}
                                    onChange={(e) => setScheduleData({ ...scheduleData, interviewDate: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                                    Lien de Visioconférence <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <Video className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                                    <input
                                        type="url"
                                        className="w-full pl-9 pr-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 focus:ring-2 focus:ring-slate-900 dark:focus:ring-white focus:border-transparent transition-all"
                                        placeholder="https://meet.google.com/..."
                                        required
                                        value={scheduleData.interviewLink}
                                        onChange={(e) => setScheduleData({ ...scheduleData, interviewLink: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="pt-4 flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowScheduleModal(false)}
                                    className="flex-1 px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 font-medium text-slate-700 dark:text-slate-300 transition-colors"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="flex-1 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-medium transition-colors shadow-sm disabled:opacity-70"
                                >
                                    {isSubmitting ? 'Enregistrement...' : 'Confirmer le RDV'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

// Helper Component for Status Badges
const StatusBadge = ({ status }) => {
    const config = {
        pending: { color: 'bg-yellow-100 text-yellow-800', label: 'En attente' },
        interview_accepted: { color: 'bg-indigo-100 text-indigo-800', label: 'Entretien Demandé' },
        interview_pending: { color: 'bg-blue-100 text-blue-800', label: 'Entretien Programmé' },
        interview_process: { color: 'bg-orange-100 text-orange-800', label: 'Entretien en cours' },
        accepted: { color: 'bg-green-100 text-green-800', label: 'Accepté' },
        rejected: { color: 'bg-red-100 text-red-800', label: 'Rejeté' },
        validated: { color: 'bg-emerald-100 text-emerald-800', label: 'Validé' },
    };

    const style = config[status] || { color: 'bg-gray-100 text-gray-800', label: status };

    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${style.color}`}>
            {style.label}
        </span>
    );
};

export default MissionApplicationsList;
